// AutopilotPage.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Coins,
  ExternalLink,
  FileText,
  Linkedin,
  Mail,
  MessageCircle,
  Mic,
  Video,
  ShieldCheck,
  Scale,
  Zap,
} from "lucide-react";
import Swal from "sweetalert2";

/** AutopilotPage – Lean, optimized version */

const TABS = { AUTOPILOT: "autopilot", MEETINGS: "meetings" };
const AUTOPILOT_PCT = 95;

const classNames = (...s) => s.filter(Boolean).join(" ");
const selectedLabel = (key) =>
  ({
    calendly: "Calendly",
    google: "Google Meet",
    whatsapp: "WhatsApp",
    event: "Event",
    custom: "Custom link",
  }[key] || "");

/* ---- Style metadata ---- */
const STYLE_INFO = {
  Conservative: {
    icon: ShieldCheck,
    theme: {
      from: "from-sky-500",
      to: "to-emerald-500",
      text: "text-sky-600",
      iconBg: "bg-sky-500/10",
    },
    desc:
      "Gentle cadence with 4 email touches and 4 LinkedIn messages. All are simple text follow-ups that prioritise deliverability.",
  },
  Balanced: {
    icon: Scale,
    theme: {
      from: "from-violet-600",
      to: "to-indigo-500",
      text: "text-violet-600",
      iconBg: "bg-violet-500/10",
    },
    desc:
      "Steady rhythm with 4 emails and 4 LinkedIn touches, adding a proposal mid-sequence and one voice note on LinkedIn.",
  },
  Aggressive: {
    icon: Zap,
    theme: {
      from: "from-fuchsia-600",
      to: "to-rose-500",
      text: "text-fuchsia-600",
      iconBg: "bg-fuchsia-500/10",
    },
    desc:
      "Short sprint with 4 emails and 4 LinkedIn messages, front-loading proposals and a voice note for maximum impact.",
  },
};

/* ---- Sequences per approach (per-step credits) ---- */
/*
  Credits assumption:
  - Simple Email: 1
  - Simple LinkedIn: 1
  - Proposal attachment: 5
  - Voice note: 3
*/

const SEQUENCES = Object.freeze({
  Conservative: [
    // Emails
    { key: "cons-email-1", channel: "email", icon: Mail, label: "Email 1 — intro", credits: 1 },
    { key: "cons-email-2", channel: "email", icon: Mail, label: "Email 2 — follow-up", credits: 1 },
    { key: "cons-email-3", channel: "email", icon: Mail, label: "Email 3 — follow-up", credits: 1 },
    { key: "cons-email-4", channel: "email", icon: Mail, label: "Email 4 — final follow-up", credits: 1 },
    // LinkedIn
    { key: "cons-li-1", channel: "linkedin", icon: Linkedin, label: "LinkedIn 1 — intro message", credits: 1 },
    { key: "cons-li-2", channel: "linkedin", icon: Linkedin, label: "LinkedIn 2 — follow-up", credits: 1 },
    { key: "cons-li-3", channel: "linkedin", icon: Linkedin, label: "LinkedIn 3 — follow-up", credits: 1 },
    { key: "cons-li-4", channel: "linkedin", icon: Linkedin, label: "LinkedIn 4 — final follow-up", credits: 1 },
  ],

  Balanced: [
    // Emails (proposal in 2nd follow-up)
    { key: "bal-email-1", channel: "email", icon: Mail, label: "Email 1 — intro", credits: 1 },
    { key: "bal-email-2", channel: "email", icon: Mail, label: "Email 2 — follow-up", credits: 1 },
    {
      key: "bal-email-3",
      channel: "email",
      icon: Mail,
      label: "Email 3 — follow-up + proposal",
      badge: "proposal",
      credits: 6, // 1 + 5
    },
    { key: "bal-email-4", channel: "email", icon: Mail, label: "Email 4 — final follow-up", credits: 1 },
    // LinkedIn (voice note in 3rd, proposal in 4th)
    { key: "bal-li-1", channel: "linkedin", icon: Linkedin, label: "LinkedIn 1 — intro message", credits: 1 },
    { key: "bal-li-2", channel: "linkedin", icon: Linkedin, label: "LinkedIn 2 — follow-up", credits: 1 },
    {
      key: "bal-li-3",
      channel: "linkedin",
      icon: Linkedin,
      label: "LinkedIn 3 — voice note message",
      badge: "voice",
      credits: 4, // 1 + 3
    },
    {
      key: "bal-li-4",
      channel: "linkedin",
      icon: Linkedin,
      label: "LinkedIn 4 — proposal message",
      badge: "proposal",
      credits: 6, // 1 + 5
    },
  ],

  Aggressive: [
    // Emails (proposal in first email)
    {
      key: "agg-email-1",
      channel: "email",
      icon: Mail,
      label: "Email 1 — intro + proposal",
      badge: "proposal",
      credits: 6, // 1 + 5
    },
    { key: "agg-email-2", channel: "email", icon: Mail, label: "Email 2 — follow-up", credits: 1 },
    { key: "agg-email-3", channel: "email", icon: Mail, label: "Email 3 — follow-up", credits: 1 },
    { key: "agg-email-4", channel: "email", icon: Mail, label: "Email 4 — final follow-up", credits: 1 },
    // LinkedIn (proposal in 2nd, voice note in 3rd)
    { key: "agg-li-1", channel: "linkedin", icon: Linkedin, label: "LinkedIn 1 — intro message", credits: 1 },
    {
      key: "agg-li-2",
      channel: "linkedin",
      icon: Linkedin,
      label: "LinkedIn 2 — proposal message",
      badge: "proposal",
      credits: 6, // 1 + 5
    },
    {
      key: "agg-li-3",
      channel: "linkedin",
      icon: Linkedin,
      label: "LinkedIn 3 — voice note message",
      badge: "voice",
      credits: 4, // 1 + 3
    },
    { key: "agg-li-4", channel: "linkedin", icon: Linkedin, label: "LinkedIn 4 — final follow-up", credits: 1 },
  ],
});

export default function AutopilotPage({ embedded = false, initialTab = TABS.AUTOPILOT }) {
  /* ------------------------------- Shell / Tabs ------------------------------- */
  const [activeTab, setActiveTab] = useState(initialTab);
  const [intentAllowed, setIntentAllowed] = useState(true); // true = show full UI

  const wrapperClass = embedded
    ? "min-h-[calc(100vh-0px)] w-full text-slate-800 bg-transparent"
    : "min-h-screen w-full text-slate-800 bg-[radial-gradient(90rem_60rem_at_10%_-20%,#f9e8ff_10%,transparent_40%),radial-gradient(80rem_60rem_at_110%_-10%,#dbeafe_10%,transparent_45%),linear-gradient(to_bottom,#ffffff,60%,#f6f7fb)]";

  // Read intent_status from localStorage once on mount
  useEffect(() => {
    const raw = localStorage.getItem("intent_status");
    if (raw !== null) {
      const allowed = raw === "true" || raw === "1";
      setIntentAllowed(allowed);
      if (!allowed) {
        setActiveTab(TABS.MEETINGS); // only show Meeting Settings when false
      }
    }
  }, []);

  /* -------------------------------- Campaigns -------------------------------- */
  const [campaignName, setCampaignName] = useState("Q2 SaaS Decision-Makers");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(
    localStorage.getItem("campaign_id") || ""
  );
  const roboman_api = process.env.REACT_APP_ROBOMAN_API;

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setCampaignLoading(true);
        const res = await fetch(`${roboman_api}/campaign/personal`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("access_token") || "",
          },
        });
        if (!res.ok) return;
        const data = await res.json();
        const mapped = (data || []).map((c) => ({ id: c.campaign_id, name: c.campaign_name }));
        setCampaigns(mapped);
      } catch (e) {
        console.error("Failed to load campaigns", e);
      } finally {
        setCampaignLoading(false);
      }
    };
    fetchCampaigns();
  }, [roboman_api]);

  useEffect(() => {
    if (!selectedCampaignId) return;
    const sel = campaigns.find((c) => String(c.id) === String(selectedCampaignId));
    if (sel?.name) {
      setCampaignName(sel.name);
      localStorage.setItem("campaign_name", sel.name);
    }
  }, [selectedCampaignId, campaigns]);

  /* ---------------------------------- Budget --------------------------------- */
  const [budget, setBudget] = useState(400); // credits
  const [prospects, setProspects] = useState(80);

  /* ------------------------------- Preferences -------------------------------- */
  const [style, setStyle] = useState("Balanced"); // Conservative | Balanced | Aggressive
  const [styleLoading, setStyleLoading] = useState(false);

  // Suggested style from backend – drives the "Suggested approach" badge
  const [suggestedStyle, setSuggestedStyle] = useState(null); // "Conservative" | "Balanced" | "Aggressive" | null

  const currentSequence = useMemo(() => SEQUENCES[style] || SEQUENCES.Balanced, [style]);

  const perProspect = useMemo(
    () => currentSequence.reduce((sum, step) => sum + (step.credits || 0), 0),
    [currentSequence]
  );

  const autopilotCap = useMemo(() => Math.floor((budget * AUTOPILOT_PCT) / 100), [budget]);
  const requiredCredits = useMemo(() => prospects * perProspect, [prospects, perProspect]);
  const estimatedUsed = useMemo(
    () => Math.min(requiredCredits, autopilotCap),
    [requiredCredits, autopilotCap]
  );

  // 🔹 On page load / campaign change: call /campaign/analyze_approach and set suggested card
  useEffect(() => {
    if (!selectedCampaignId) return;

    const analyzeApproach = async () => {
      try {
        setStyleLoading(true);

        const res = await fetch(`${roboman_api}/campaign/analyze_approach`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("access_token") || "",
            request_user_id: localStorage.getItem("user_id") || "",
          },
          body: JSON.stringify({
            // keep same type as you use elsewhere
            Campaign_id: selectedCampaignId,
          }),
        });

        if (!res.ok) {
          const errBody = await res.text();
          console.error("Failed to analyze approach", res.status, errBody);
          return;
        }

        const data = await res.json();
        console.log("analyze_approach response", data);

        // ---- extract suggested approach in a forgiving way ----
        let suggestion = null;

        if (typeof data === "string") {
          suggestion = data;
        } else if (data) {
          if (data.suggested_approach) suggestion = data.suggested_approach;
          else if (data.approach) suggestion = data.approach;
          else if (data.result?.suggested_approach) suggestion = data.result.suggested_approach;
          else if (data.result?.approach) suggestion = data.result.approach;
        }

        // Handle shape like { suggested_approach: { approach: "balanced" } }
        if (suggestion && typeof suggestion === "object" && suggestion.approach) {
          suggestion = suggestion.approach;
        }

        if (!suggestion) {
          console.warn("No suggested_approach found in response");
          return;
        }

        const normalized = String(suggestion).toLowerCase();

        let mapped = null;
        if (normalized.includes("conservative")) mapped = "Conservative";
        else if (normalized.includes("balanced")) mapped = "Balanced";
        else if (normalized.includes("aggressive")) mapped = "Aggressive";

        if (!mapped) {
          console.warn("Could not map suggested approach from value:", suggestion);
          return;
        }

        console.log("Resolved suggested approach:", mapped);

        // ✅ make that card active + show badge
        setStyle(mapped);
        setSuggestedStyle(mapped);
      } catch (err) {
        console.error("Error analyzing approach", err);
      } finally {
        setStyleLoading(false);
      }
    };

    analyzeApproach();
  }, [selectedCampaignId, roboman_api]);

  // 🔹 When user switches style, update DB via /campaign/update_approach
  const handleStyleChange = useCallback(
    async (newStyle) => {
      // If they click the same style again, ignore
      if (newStyle === style) return;

      // Optimistic UI: switch card immediately
      setStyle(newStyle);

      if (!selectedCampaignId) return;

      try {
        const res = await fetch(`${roboman_api}/campaign/update_approach`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("access_token") || "",
            request_user_id: localStorage.getItem("user_id") || "",
          },
          body: JSON.stringify({
            Campaign_id: selectedCampaignId,
            approach: newStyle.toLowerCase(), // conservative | balanced | aggressive
          }),
        });

        if (!res.ok) {
          const errBody = await res.text();
          console.error("Failed to update approach", res.status, errBody);
        }
      } catch (err) {
        console.error("Error updating approach", err);
      }
    },
    [roboman_api, selectedCampaignId, style]
  );

  const suggestion = useMemo(() => {
    const styleCopy =
      style === "Balanced"
        ? "We recommend a Balanced engagement cadence for steady reply rates."
        : style === "Conservative"
        ? "Use a lighter cadence with Conservative engagement to reduce send volume."
        : "Aggressive engagement can lift responses for short sprints; monitor budget closely.";
    return styleCopy;
  }, [style]);

  /* ---------------------- Launch Autopilot handler --------------------------- */
  const handleAutoPilot = useCallback(async () => {
    if (!selectedCampaignId) {
      await Swal.fire({
        title: "Select a campaign",
        text: "Please choose a campaign before launching Autopilot.",
        icon: "warning",
      });
      return;
    }

    const prompt_type = "email_generation_v5"; // always use default

    try {
      const response = await fetch(`${roboman_api}/campaign/${selectedCampaignId}/outreach/auto`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: localStorage.getItem("access_token") || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt_name: prompt_type }),
      });

      if (!response.ok) {
        await Swal.fire({
          title: "Autopilot failed",
          text: "We encountered an issue while processing Autopilot. Please try again later.",
          icon: "error",
        });
        return;
      }

      await Swal.fire({
        title: "Autopilot started",
        text: "Autopilot is now processing your request. You’ll be taken to the email status page.",
        icon: "success",
      });

      // redirect to email status page after success
      window.location.href = "/email-status";
    } catch (e) {
      console.error("Autopilot error", e);
      await Swal.fire({
        title: "Autopilot failed",
        text: "We encountered an issue while processing Autopilot. Please try again later.",
        icon: "error",
      });
    }
  }, [roboman_api, selectedCampaignId]);

  /* ------------------------------ Meetings state ------------------------------ */
  const [selected, setSelected] = useState(null); // 'calendly' | 'google' | 'whatsapp' | 'event' | 'custom'
  const [calendlyUrl, setCalendlyUrl] = useState("https://calendly.com/your-team/30min");
  const [meetUrl, setMeetUrl] = useState("");
  const [waNumber, setWaNumber] = useState("");
  const [waMessage, setWaMessage] = useState("Hi! I'd like to book a call.");
  const [eventDetails, setEventDetails] = useState("");
  const [customUrl, setCustomUrl] = useState("");

  const openCalendly = useCallback(
    () => calendlyUrl && window.open(calendlyUrl, "_blank"),
    [calendlyUrl]
  );
  const openMeet = useCallback(() => meetUrl && window.open(meetUrl, "_blank"), [meetUrl]);
  const openWhatsApp = useCallback(() => {
    if (!waNumber) return;
    const link = `https://wa.me/${waNumber.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
      waMessage
    )}`;
    window.open(link, "_blank");
  }, [waNumber, waMessage]);

  // 🔹 Save meeting method + value (+ WhatsApp message as link_subtext) to backend via /campaign/update_link_type
  const handleMeetingContinue = useCallback(async () => {
    if (!selected) return;

    if (!selectedCampaignId) {
      await Swal.fire({
        title: "Select a campaign",
        text: "Please choose a campaign before saving meeting settings.",
        icon: "warning",
      });
      return;
    }

    let link_type = "";
    let link_text = "";
    let link_subtext = ""; // ✅ NEW (WhatsApp message)

    if (selected === "calendly") {
      link_type = "calendly";
      link_text = (calendlyUrl || "").trim();
    } else if (selected === "google") {
      link_type = "google_meet";
      link_text = (meetUrl || "").trim();
    } else if (selected === "whatsapp") {
      link_type = "whatsapp";
      link_text = (waNumber || "").trim();
      link_subtext = (waMessage || "").trim(); // ✅ send WhatsApp message too
    } else if (selected === "event") {
      link_type = "event";
      link_text = (eventDetails || "").trim();
    } else if (selected === "custom") {
      link_type = "custom_link";
      link_text = (customUrl || "").trim();
    }

    if (!link_text) {
      await Swal.fire({
        title: "Missing details",
        text: "Please enter a link or value for the selected method before continuing.",
        icon: "warning",
      });
      return;
    }

    try {
      const res = await fetch(`${roboman_api}/campaign/update_link_type`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("access_token") || "",
          request_user_id: localStorage.getItem("user_id") || "",
        },
        body: JSON.stringify({
          Campaign_id: selectedCampaignId,
          link_type,
          link_text,
          link_subtext, // ✅ NEW FIELD
        }),
      });

      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`API error ${res.status}: ${errBody}`);
      }

      const data = await res.json();
      if (data.status !== "success") {
        throw new Error(data.Error || "Failed to update meeting settings.");
      }

      await Swal.fire({
        icon: "success",
        title: "Meeting settings saved",
        text: "Your preferred meeting method has been updated for this campaign.",
        timer: 1600,
        showConfirmButton: false,
      });

      if (intentAllowed) {
        setActiveTab(TABS.AUTOPILOT);
      }
    } catch (err) {
      console.error("update_link_type error", err);
      await Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err.message || "Something went wrong while saving meeting settings.",
      });
    }
  }, [
    selected,
    selectedCampaignId,
    calendlyUrl,
    meetUrl,
    waNumber,
    waMessage, // ✅ NEW DEP
    eventDetails,
    customUrl,
    roboman_api,
    intentAllowed,
  ]);

  // shared condition for "Continue" + launch button in Meetings
  const meetingConfigIncomplete =
    !selected ||
    (selected === "calendly" && !calendlyUrl.trim()) ||
    (selected === "google" && !meetUrl.trim()) ||
    (selected === "whatsapp" && !waNumber.trim()) ||
    (selected === "event" && !eventDetails.trim()) ||
    (selected === "custom" && !customUrl.trim());

  /* ----------------------------------- UI ------------------------------------ */
  return (
    <div className={wrapperClass}>
      {!embedded && (
        <div className="sticky top-0 z-30 bg-white/70 backdrop-blur border-b border-slate-200">
          <div className="mx-auto max-w-7xl px-6 py-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-amber-200 to-yellow-300 grid place-items-center shadow ring-1 ring-amber-300/40">
              <Coins className="h-5 w-5 text-slate-900" />
            </div>
            <h1 className=" text-xl md:text-2xl font-extrabold font-Inter opacity-1 bg-gradient-to-r from-gray-700 via-blue-700 to-fuchsia-700  bg-clip-text text-transparent ">
              Autopilot Settings
            </h1>
          </div>
        </div>
      )}

      {/* Campaign selector */}
      <div className="mx-auto max-w-7xl px-6 pt-6">
        <section className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-700">
              Choose existing campaign
            </span>
            <div className="relative max-w-xl">
              <select
                value={selectedCampaignId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCampaignId(val);
                  localStorage.setItem("campaign_id", val);
                }}
                disabled={campaignLoading || campaigns.length === 0}
                className="w-full appearance-none rounded-2xl bg-white text-slate-900 px-4 py-3.5 pr-9 shadow-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500  disabled:bg-slate-100 disabled:text-slate-400  "
                aria-label="Select campaign"
              >
                <option value="">
                  {campaignLoading
                    ? "Loading…"
                    : campaigns.length
                    ? "— Select campaign —"
                    : "— No campaigns found —"}
                </option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            </div>
          </label>
        </section>
      </div>

      {/* Tabs */}
      <div className="mx-auto max-w-7xl px-6 pt-4">
        <nav
          className="flex items-center gap-2 p-1 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm w-fit"
          aria-label="Autopilot tabs"
        >
          {intentAllowed && (
            <button
              type="button"
              onClick={() => setActiveTab(TABS.AUTOPILOT)}
              className={classNames(
                "px-3.5 py-2 rounded-xl text-sm font-medium transition",
                activeTab === TABS.AUTOPILOT
                  ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow"
                  : "text-slate-700 hover:bg-slate-100"
              )}
              aria-pressed={activeTab === TABS.AUTOPILOT}
            >
              Autopilot Setup
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab(TABS.MEETINGS)}
            className={classNames(
              "px-3.5 py-2 rounded-xl text-sm font-medium transition",
              activeTab === TABS.MEETINGS
                ? "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow"
                : "text-slate-700 hover:bg-slate-100"
            )}
            aria-pressed={activeTab === TABS.MEETINGS}
          >
            Meeting Settings
          </button>
        </nav>
      </div>

      <main className="mx-auto max-w-7xl px-6 py-10">
        {/* ----------------------------- Autopilot Tab ---------------------------- */}
        {intentAllowed && (
          <div className={activeTab === TABS.AUTOPILOT ? "" : "hidden"}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start lg:items-stretch">
              {/* Left column */}
              <section className="lg:col-span-2 flex flex-col gap-6 h-full">
                {/* Engagement Style */}
                <Card title="Engagement style" subtitle={styleLoading ? "Detecting best approach..." : style}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 [isolation:isolate]">
                    {["Conservative", "Balanced", "Aggressive"].map((opt) => {
                      const { icon: Icon, theme, desc } = STYLE_INFO[opt];
                      const seq = SEQUENCES[opt];
                      return (
                        <EngagementOption
                          key={opt}
                          active={style === opt}
                          isSuggested={suggestedStyle === opt}
                          title={opt}
                          Icon={Icon}
                          theme={theme}
                          description={desc}
                          sequence={seq}
                          onClick={() => handleStyleChange(opt)}
                        />
                      );
                    })}
                  </div>
                  <p className="mt-5 text-sm text-slate-600">{suggestion}</p>
                </Card>

                {/* Sequence Preview – driven by current style */}
                <Card title="Sequence Preview">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <SequenceGroup title="Emails" steps={currentSequence.filter((s) => s.channel === "email")} />
                    <SequenceGroup title="LinkedIn" steps={currentSequence.filter((s) => s.channel === "linkedin")} />
                  </div>
                </Card>
              </section>

              {/* Right column */}
              <aside className="flex flex-col gap-6 h-full">
                <Card title="AI Email Crafting" tag={<TagPill>Inbox-ready Emails</TagPill>}>
                  <p className="text-sm leading-6 text-slate-700">{suggestion}</p>
                  <ul className="mt-4 space-y-2 text-xs text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Use concise subject lines for SaaS buyers.
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Personalize with company pain-points by industry.
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Send Proposal after first reply to lift conversions.
                    </li>
                  </ul>
                </Card>

                <Card title="Multichannel Autopilot" tag={<TagPill>Active</TagPill>}>
                  <div className="grid grid-cols-3 gap-3">
                    <MiniFeature title="Email" status="Active" />
                    <MiniFeature title="LinkedIn" status="Active" />
                    <MiniFeature title="Voice" status="Active" />
                  </div>
                </Card>

                <div className="mt-auto">
                  <Card>
                    <button
                      type="button"
                      className="mt-1 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 font-semibold text-white shadow-md hover:from-violet-500 hover:to-fuchsia-400 active:scale-[.99] transition"
                      onClick={handleAutoPilot}
                      aria-label="Launch Autopilot"
                    >
                      Launch Autopilot
                    </button>
                    <p className="mt-3 text-center text-xs text-slate-500">
                      You can review status in the email status view.
                    </p>
                  </Card>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* ----------------------------- Meetings Tab ---------------------------- */}
        <div className={activeTab === TABS.MEETINGS ? "" : "hidden"}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start lg:items-stretch">
            <section className="lg:col-span-2 space-y-6">
              <Card title="Choose a method" tag={<TagPill>Multichannel</TagPill>}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MethodCard
                    active={selected === "calendly"}
                    onClick={() => setSelected("calendly")}
                    icon={<CalendarDays className="h-5 w-5" />}
                    title="Calendly"
                    subtitle="Use your existing scheduling link"
                    badge="Recommended"
                  />
                  <MethodCard
                    active={selected === "google"}
                    onClick={() => setSelected("google")}
                    icon={<Video className="h-5 w-5" />}
                    title="Google Meet"
                    subtitle="Share a Meet or Calendar link"
                  />
                  <MethodCard
                    active={selected === "whatsapp"}
                    onClick={() => setSelected("whatsapp")}
                    icon={<MessageCircle className="h-5 w-5" />}
                    title="WhatsApp"
                    subtitle="Start a chat to coordinate"
                  />
                  <MethodCard
                    active={selected === "event"}
                    onClick={() => setSelected("event")}
                    icon={<Clock3 className="h-5 w-5" />}
                    title="Event"
                    subtitle="Create a simple calendar event"
                  />
                  {/* New: Custom link method */}
                  <MethodCard
                    active={selected === "custom"}
                    onClick={() => setSelected("custom")}
                    icon={<ExternalLink className="h-5 w-5" />}
                    title="Custom link"
                    subtitle="Any website, landing page, or URL"
                  />
                </div>

                {selected && (
                  <div className="mt-6 border-t border-slate-200 pt-5">
                    {selected === "calendly" && (
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">Calendly</h4>
                          <TagPill>Active</TagPill>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                          <input
                            value={calendlyUrl}
                            onChange={(e) => setCalendlyUrl(e.target.value)}
                            placeholder="https://calendly.com/your-team/30min"
                            className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="Calendly link"
                          />
                          <button
                            type="button"
                            onClick={openCalendly}
                            className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 text-white font-semibold shadow hover:from-violet-500 hover:to-fuchsia-400"
                          >
                            Open <ExternalLink className="inline h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    )}

                    {selected === "google" && (
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">Google Meet</h4>
                          <TagPill>Manual link</TagPill>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                          <input
                            value={meetUrl}
                            onChange={(e) => setMeetUrl(e.target.value)}
                            placeholder="https://meet.google.com/xxx-xxxx-xxx"
                            className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="Google Meet URL"
                          />
                          <button
                            type="button"
                            onClick={openMeet}
                            className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 text-white font-semibold shadow hover:from-violet-500 hover:to-fuchsia-400"
                          >
                            Open <ExternalLink className="inline h-4 w-4 ml-1" />
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Tip: create a Meet via Google Calendar and paste the link here.
                        </p>
                      </div>
                    )}

                    {selected === "whatsapp" && (
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">WhatsApp</h4>
                          <TagPill>Chat</TagPill>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <input
                            value={waNumber}
                            onChange={(e) => setWaNumber(e.target.value)}
                            placeholder="Phone number (e.g. +919999999999)"
                            className="rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="WhatsApp phone number"
                          />
                          <input
                            value={waMessage}
                            onChange={(e) => setWaMessage(e.target.value)}
                            placeholder="Intro message"
                            className="md:col-span-2 rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="WhatsApp message"
                          />
                        </div>
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={openWhatsApp}
                            className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 text-white font-semibold shadow hover:from-violet-500 hover:to-fuchsia-400"
                          >
                            Start Chat
                          </button>
                        </div>
                      </div>
                    )}

                    {selected === "event" && (
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">Event</h4>
                          <TagPill>Manual link</TagPill>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                          <input
                            value={eventDetails}
                            onChange={(e) => setEventDetails(e.target.value)}
                            placeholder="Add event link or details"
                            className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="Event link or details"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const value = eventDetails.trim();
                              if (!value) return;
                              if (/^https?:\/\//i.test(value)) {
                                window.open(value, "_blank");
                              }
                            }}
                            className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 text-white font-semibold shadow hover:from-violet-500 hover:to-fuchsia-400"
                          >
                            Open <ExternalLink className="inline h-4 w-4 ml-1" />
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          This can be a calendar event link or meeting location. We'll store it with your campaign.
                        </p>
                      </div>
                    )}

                    {selected === "custom" && (
                      <div>
                        <div className="mb-3 flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-slate-900">Custom link</h4>
                          <TagPill>Flexible</TagPill>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                          <input
                            value={customUrl}
                            onChange={(e) => setCustomUrl(e.target.value)}
                            placeholder="https://your-website-or-booking-link.com"
                            className="w-full rounded-2xl bg-white text-slate-900 px-4 py-3.5 shadow-sm ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none"
                            aria-label="Custom link URL"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const value = customUrl.trim();
                              if (!value) return;
                              const url = /^https?:\/\//i.test(value) ? value : `https://${value}`;
                              window.open(url, "_blank");
                            }}
                            className="rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-500 px-5 py-3.5 text-white font-semibold shadow hover:from-violet-500 hover:to-fuchsia-400"
                          >
                            Open <ExternalLink className="inline h-4 w-4 ml-1" />
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500">
                          Use this for any type of link — your website, booking page, form, demo page, or any other URL you
                          want to share with prospects.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </section>

            {/* Summary */}
            <aside className="space-y-6">
              <Card title="Summary" tag={<TagPill>{selected ? "Selected" : "Choose method"}</TagPill>}>
                <div className="space-y-3 text-sm">
                  <Row label="Method" value={selected ? selectedLabel(selected) : "—"} />
                  {selected === "calendly" && <Row label="Link" value={calendlyUrl || "—"} />}
                  {selected === "google" && <Row label="Meet URL" value={meetUrl || "—"} />}
                  {selected === "whatsapp" && (
                    <>
                      <Row label="WhatsApp" value={waNumber || "—"} />
                      <Row label="Message" value={waMessage || "—"} /> {/* ✅ NEW */}
                    </>
                  )}
                  {selected === "event" && <Row label="Event details" value={eventDetails || "—"} />}
                  {selected === "custom" && <Row label="Custom URL" value={customUrl || "—"} />}
                </div>

                {/* Save meeting settings */}
                <button
                  type="button"
                  disabled={meetingConfigIncomplete}
                  onClick={handleMeetingContinue}
                  className={classNames(
                    "mt-5 w-full rounded-2xl px-5 py-3.5 font-semibold shadow-md transition",
                    meetingConfigIncomplete
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:from-violet-500 hover:to-fuchsia-400"
                  )}
                  aria-disabled={meetingConfigIncomplete}
                >
                  Continue
                </button>

                {/* When intent_status is false: also show Launch Autopilot here */}
                {!intentAllowed && (
                  <button
                    type="button"
                    onClick={handleAutoPilot}
                    disabled={meetingConfigIncomplete}
                    className={classNames(
                      "mt-3 w-full rounded-2xl px-5 py-3.5 font-semibold shadow-md transition",
                      meetingConfigIncomplete
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:from-violet-500 hover:to-fuchsia-400"
                    )}
                    aria-disabled={meetingConfigIncomplete}
                  >
                    Launch Autopilot
                  </button>
                )}
              </Card>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}

/* --------------------------------- Helpers -------------------------------- */
function Card({ title, subtitle, icon, tag, children }) {
  const hasHeader = title || subtitle || tag;

  return (
    <section className="relative h-full rounded-[22px] p-[1.5px] bg-gradient-to-br from-white to_white shadow-[0_24px_80px_-30px_rgba(99,102,241,0.35)]">
      <div className="rounded-[20px] bg-white/80 backdrop-blur-md ring-1 ring-violet-200/60 p-5 flex flex-col h-full">
        {hasHeader && (
          <div className="mb-4 flex items-end justify-between gap-4">
            <div className="flex items-center gap-3">
              {icon && <div className="text-slate-600">{icon}</div>}
              {title && <h3 className="text-sm font-semibold tracking-wide text-slate-900">{title}</h3>}
              {tag && <div className="ml-2">{tag}</div>}
            </div>
            {subtitle && <span className="text-sm font-medium text-slate-700 text-right">{subtitle}</span>}
          </div>
        )}
        <div className={hasHeader ? "mt-1 flex-1 flex flex-col" : "flex-1 flex flex-col"}>{children}</div>
      </div>
    </section>
  );
}

function TagPill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-[11px] px-3 py-1 shadow">
      {children}
    </span>
  );
}

function Badge({ children, tone = "default" }) {
  const tones = {
    default: "bg-slate-100 text-slate-800 ring-1 ring-slate-200",
    success: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    warning: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  };
  const toneClass = tones[tone] || tones.default;
  return (
    <span className={classNames("inline-flex items-center rounded-xl px-2.5 py-1 text-xs font-semibold", toneClass)}>
      {children}
    </span>
  );
}

/* Engagement option: description + compact summary rows (no detailed sequence) */
function EngagementOption({ active, isSuggested, title, Icon, theme, description, sequence, onClick }) {
  const emailSteps = (sequence || []).filter((s) => s.channel === "email");
  const linkedinSteps = (sequence || []).filter((s) => s.channel === "linkedin");

  const emailHasProposal = emailSteps.some((s) => s.badge === "proposal");
  const emailHasVoice = emailSteps.some((s) => s.badge === "voice");

  const linkedinHasProposal = linkedinSteps.some((s) => s.badge === "proposal");
  const linkedinHasVoice = linkedinSteps.some((s) => s.badge === "voice");

  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "relative group w-full h-full rounded-[20px] p-[1.5px] transition-transform overflow-visible",
        active
          ? `bg-gradient-to-r ${theme.from} ${theme.to} shadow-[0_18px_70px_-28px_rgba(139,92,246,0.55)] translate-y-[-1px] z-10`
          : isSuggested ? "bg-slate-200/60 hover:translate-y-[-1px] z-20" : "bg-slate-200/60 hover:translate-y-[-1px] z-0"
      )}
      aria-pressed={active}
    >
      <div
        className={classNames(
          "relative rounded-[18px] bg-white/90 backdrop-blur ring-1 p-4 h-full overflow-visible",
          active ? "ring-transparent" : "ring-slate-200"
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className={classNames("h-9 w-9 grid place-items-center rounded-xl", theme.iconBg, theme.text)}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">{title}</div>
              <p className="mt-0.5 text-xs leading-5 text-slate-600">{description}</p>
            </div>
          </div>

          {/* "Suggested approach" badge */}
          {isSuggested && (
            <span className="relative z-20 inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2 py-0.5 text-[11px]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Suggested approach
            </span>
          )}
        </div>

        {/* Compact summaries – no per-step timeline here */}
        <div className="mt-3 space-y-1.5">
          <SummaryRow channel="email" count={emailSteps.length} hasProposal={emailHasProposal} hasVoice={emailHasVoice} />
          <SummaryRow
            channel="linkedin"
            count={linkedinSteps.length}
            hasProposal={linkedinHasProposal}
            hasVoice={linkedinHasVoice}
          />
        </div>
      </div>
    </button>
  );
}

/* Summary row inside engagement cards */
function SummaryRow({ channel, count, hasProposal, hasVoice }) {
  const isEmail = channel === "email";
  const IconComponent = isEmail ? Mail : Linkedin;
  const bubbleColors = isEmail ? "bg-sky-50 text-sky-700 ring-sky-100" : "bg-violet-50 text-violet-700 ring-violet-100";
  const label = isEmail ? "Emails" : "LinkedIn";

  let extras = "";
  if (hasProposal && hasVoice) extras = " • includes proposal & voice note";
  else if (hasProposal) extras = " • includes proposal";
  else if (hasVoice) extras = " • includes voice note";

  return (
    <div className="flex items-center gap-2 text-[11px] text-slate-600">
      <div className={classNames("h-7 w-7 rounded-full grid place-items-center ring-1", bubbleColors)}>
        <IconComponent className="h-3.5 w-3.5" />
      </div>
      <span className="font-medium text-slate-800">
        {count} {label}
      </span>
      {extras && <span className="text-slate-500">{extras}</span>}
    </div>
  );
}

/* Sequence preview groups */
function SequenceGroup({ title, steps }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
        <span className="text-[11px] text-slate-500">{steps.length} touches</span>
      </div>
      <div className="flex flex-wrap gap-3">
        {steps.map((step, idx) => (
          <div key={step.key} className="flex flex-col items-center gap-1">
            <StepIcon Icon={step.icon} channel={step.channel} index={idx + 1} badge={step.badge} label={step.label} />
            <span className="text-[10px] text-slate-500 font-medium">{idx + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Single circular icon for steps */
function StepIcon({ Icon, channel, index, badge, size = "md", label }) {
  const isEmail = channel === "email";
  const circleSize = size === "sm" ? "h-7 w-7" : "h-10 w-10";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  const baseColors = isEmail ? "bg-sky-50 text-sky-700 ring-sky-100" : "bg-violet-50 text-violet-700 ring-violet-100";

  const isProposal = badge === "proposal";
  const isVoice = badge === "voice";

  return (
    <div className={classNames("relative rounded-full grid place-items-center ring-1", circleSize, baseColors)} title={label}>
      <Icon className={iconSize} />
      {(isProposal || isVoice) && (
        <span className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-slate-900 text-white grid place-items-center">
          {isProposal ? <FileText className="h-2.5 w-2.5" /> : <Mic className="h-2.5 w-2.5" />}
        </span>
      )}
    </div>
  );
}

function MiniFeature({ title, status }) {
  return (
    <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-3 text-center">
      <div className="text-sm font-medium text-slate-800">{title}</div>
      <div className="mt-1 text-xs text-emerald-600 font-semibold">{status}</div>
    </div>
  );
}

function MethodCard({ active, onClick, icon, title, subtitle, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "text-left rounded-[20px] p-4 bg-white/80 backdrop-blur ring-1 shadow-sm transition w-full h-full",
        active ? "ring-violet-400 shadow-[0_12px_40px_-16px_rgba(139,92,246,0.35)]" : "ring-slate-200 hover:ring-violet-300"
      )}
      aria-pressed={active}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={classNames(
              "h-9 w-9 grid place-items-center rounded-xl",
              active ? "bg-gradient-to-tr from-violet-600 to-fuchsia-500 text-white" : "bg-slate-100 text-slate-700"
            )}
          >
            {icon}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            <div className="text-xs text-slate-600 mt-0.5">{subtitle}</div>
          </div>
        </div>
        {badge && <span className="text-[11px] rounded-full bg-slate-900 text-white px-2 py-0.5">{badge}</span>}
      </div>
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-slate-700">
      <span>{label}</span>
      <span className="font-medium text-slate-900 truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}
//Nothing to commit